/** Platform admin: ai-models screen (P9, mockups/admin/ai-models.html). Owned by the ai-models screen; fil mirrors every key. */
export const aiModels = {
  title: "AI providers & models",
  subtitle: "LLM gateway, embeddings, rerankers, guardrails and the semantic cache shared by all tenants.",
  changeHistory: "Change history",
  addProvider: "Add provider",
  addProviderUnavailable: "Adding providers isn't available yet — providers are configured at deploy time",
  stats: {
    label: "AI platform summary (last 30 days)",
    llmSpend: "LLM spend (30d)",
    llmSpendFoot: "≈ {{php}} · all AI {{total}}",
    perDay: "Messages / day",
    perDayFoot: "{{conversations}} conversations in 30 days",
    cacheHit: "Cache hit rate",
    cacheHitFoot: "saved ≈ {{saved}} (30d)",
    providers: "Providers enabled",
    providersFoot: "{{count}} operational",
  },
  tabs: {
    label: "AI platform sections",
    providers: "Providers",
    chat: "Chat models",
    embeddings: "Embeddings",
    rerankers: "Rerankers",
    guardrails: "Guardrails & moderation",
    cache: "Semantic cache",
  },
  status: { operational: "Operational", degraded: "Elevated latency", down: "Down", unknown: "Unknown" },
  kinds: { chat: "Chat", embedding: "Embeddings", rerank: "Rerank", vision: "Vision" },
  providers: {
    callout: "All calls go through the Sagot LLM gateway. Provider keys live in the deployment secret store and are never sent to tenants — this page only shows their masked status.",
    listLabel: "AI providers",
    enable: "Enable {{name}} for routing",
    enabled: "{{name}} enabled for routing",
    disabled: "{{name}} disabled — traffic moves to fallbacks",
    statusLabel: "Status",
    apiKey: "API key",
    notConfigured: "Not configured",
    rotate: "Rotate",
    rotateFor: "Rotate {{name}} key",
    rotated: "Rotated {{when}} · next due {{due}}",
    rotatedNoDue: "Rotated {{when}}",
    neverRotated: "No rotation recorded yet",
    region: "Region",
    models: "Models",
    modelCount_one: "{{count}} model",
    modelCount_other: "{{count}} models",
    modelList: "Models: {{list}}",
    noModels: "No models registered",
    test: "Test connection",
    testUnavailable: "Connection tests aren't available yet",
    noCapabilities: "No models",
    emptyTitle: "No providers configured",
    emptyBody: "Providers are registered when the gateway is deployed. Check the deployment configuration.",
    disableTitle: "Disable {{name}}?",
    disableBody: "Requests routed to {{name}} move to the next model in each fallback chain. Tenants pinned to its models may see slower or failed answers.",
    disableConfirm: "Disable provider",
  },
  rotate: {
    title: "Rotate key · {{name}}",
    description: "Replace the key in the deployment secret store first, then record the rotation here. Sagot stores only the last 4 characters.",
    last4: "Last 4 characters of the new key",
    last4Hint: "Letters and digits only, e.g. a91F.",
    last4Invalid: "Enter exactly 4 letters or digits.",
    note: "Note (optional)",
    notePlaceholder: "e.g. Scheduled quarterly rotation",
    warning: "Logged as api_key.rotated. The full key is never shown or stored by Sagot.",
    submit: "Rotate key",
    done: "Key rotation recorded for {{name}}",
  },
  chat: {
    title: "Chat models",
    hint: "Prices per 1M tokens (USD). Plan access controls which models tenants can pick in agent settings.",
    tableLabel: "Chat models",
    model: "Model",
    provider: "Provider",
    context: "Context",
    input: "Input / 1M",
    output: "Output / 1M",
    enabledCol: "Enabled",
    actionsCol: "Default",
    default: "Default",
    disabledBadge: "Disabled",
    makeDefault: "Make default",
    makeDefaultFor: "Make {{name}} the default",
    madeDefault: "{{name}} is now the platform default",
    enableModel: "Enable {{name}}",
    modelEnabled: "{{name}} enabled",
    modelDisabled: "{{name}} disabled",
    accessFor: "{{plan}} access to {{model}}",
    saveAccess: "Save access",
    accessSaved_one: "Plan access saved for {{count}} model",
    accessSaved_other: "Plan access saved for {{count}} models",
    noChanges: "No changes to save",
    addModel: "Add model",
    emptyTitle: "No chat models yet",
    emptyBody: "Register a provider model to make it available to routing and plans.",
  },
  routing: {
    title: "Routing chains",
    hint: "Each routing key tries its models top to bottom. The gateway falls back on timeout, rate limits (429) or provider errors (5xx).",
    listLabel: "Routing keys",
    edit: "Edit",
    editing: "Editing",
    steps_one: "{{count}} model",
    steps_other: "{{count}} models",
    orderTitle: "Fallback order",
    orderHint: "Use the arrows to reorder. The first model is the primary.",
    orderFor: "Fallback order for {{key}}",
    primary: "Primary",
    fallback: "Fallback {{n}}",
    moveUp: "Move {{name}} up",
    moveDown: "Move {{name}} down",
    remove: "Remove {{name}}",
    addLabel: "Add a model to the chain",
    addPlaceholder: "Choose a model…",
    add: "Add",
    save: "Save routing",
    saved: "Routing saved for {{key}} · logged as model.routing.updated",
    reset: "Reset",
    unsaved: "Unsaved changes",
    maxSteps: "A chain holds at most {{count}} models.",
    disabledModel: "Disabled",
    emptyTitle: "No routing keys",
    emptyBody: "Routing keys are created by the gateway seed. Check the deployment.",
  },
  models: {
    title: "Add model",
    description: "Register a provider model. New models start disabled unless you enable them now.",
    provider: "Provider",
    kind: "Type",
    displayName: "Display name",
    modelKey: "Model key",
    modelKeyHint: "The provider's model id, e.g. gpt-5-mini.",
    modelKeyInvalid: "Use letters, digits, dots, dashes, @ or underscores (max 80).",
    tier: "Tier",
    tierNone: "No tier",
    tiers: { standard: "Standard", premium: "Premium" },
    context: "Context window (tokens)",
    dimensions: "Dimensions",
    inputPrice: "Input / 1M (USD)",
    outputPrice: "Output / 1M (USD)",
    numberInvalid: "Enter a positive number.",
    nameRequired: "Enter a display name.",
    providerRequired: "Choose a provider.",
    enableNow: "Enable now",
    enableNowHint: "Otherwise the model is created disabled.",
    submit: "Add model",
    done: "{{name}} added",
  },
  embeddings: {
    defaultTitle: "Default embedding model",
    inRouting: "Primary in routing",
    noDefaultTitle: "No default embedding model",
    noDefaultBody: "Add an embedding model and put it first in the embedding routing chain.",
    dims: "@{{dims}}d",
    dimensions: "Dimensions",
    cost: "Cost",
    perMillion: "/ 1M tok",
    provider: "Provider",
    verified: "Price verified",
    pipelineTitle: "Ingestion pipeline",
    pipeline: {
      parse: "Parse + OCR (layout, tables, headings)",
      chunk: "Structure-aware chunking with overlap",
      context: "Contextual retrieval: a small model writes context per chunk",
      embed: "Embed → vector column + full-text index",
    },
    alternativesTitle: "Alternatives",
    alternativesEmptyTitle: "No alternative models",
    alternativesEmptyBody: "Register another embedding model to compare or migrate tenants to it.",
    migrate: "Migrate a tenant",
    altMeta: "{{provider}} · {{dims}}",
    altDisabled: "Disabled",
  },
  migrations: {
    title: "Re-embed migration",
    hint: "Vectors from different models are not comparable, so switching models means re-embedding every chunk of a tenant into a new index.",
    new: "New migration",
    steps: {
      shadow: { title: "Create shadow column + index", body: "Built concurrently next to the live index" },
      backfill: { title: "Backfill via embedding workers", body: "Reuses stored chunk text; new uploads dual-write" },
      evaluate: { title: "Run golden-set evaluation", body: "Cutover blocked if recall drops" },
      cutover: { title: "Atomic cutover, keep old index", body: "Rollback flips the tenant's embedding version" },
    },
    listLabel: "Migration jobs",
    allTenants: "All tenants",
    unknownTenant: "Unknown tenant",
    unknownModel: "Unknown model",
    jobTitle: "{{tenant}} → {{model}}",
    jobMeta: "job {{id}} · created {{when}}",
    progress: "{{done}} / {{total}} chunks",
    progressLabel: "Re-embed progress for {{tenant}}",
    dimensions: "Dimensions",
    started: "Started",
    estCost: "Est. cost",
    notStarted: "Not started",
    states: { queued: "Queued", running: "Running", paused: "Paused", cancelled: "Cancelled", done: "Done", failed: "Failed" },
    pause: "Pause",
    resume: "Resume",
    cancel: "Cancel & drop shadow index",
    paused: "Migration paused · workers finish the current batch",
    resumed: "Migration resumed",
    cancelled: "Migration cancelled · shadow index dropped",
    viewQueue: "View queue",
    emptyTitle: "No re-embed migrations",
    emptyBody: "Start one to move a tenant (or everyone) to another embedding model.",
    cancelTitle: "Cancel migration?",
    cancelBody: "{{done}} re-embedded chunks and the shadow index will be dropped. {{tenant}} keeps using its current model. Embedding cost already spent is not recoverable.",
    keepRunning: "Keep running",
    cancelConfirm: "Cancel migration",
    dialogTitle: "New re-embed migration",
    dialogDescription: "Queues a job on the embedding workers. Tenants keep answering from the current index until cutover.",
    tenant: "Tenant",
    target: "Target model",
    targetRequired: "Choose an embedding model.",
    noTargets: "Register an embedding model first.",
    runWindow: "Run window",
    windows: { now: "Now", off_peak: "Off-peak tonight (11 PM – 5 AM PHT)" },
    evalGate: "Run golden-set eval and block cutover if recall drops",
    notifyOwner: "Notify tenant owner when complete",
    submit: "Queue migration",
    queued: "Migration queued · {{chunks}} chunks",
  },
  rerankers: {
    pipelineTitle: "Retrieval pipeline",
    pipelineLabel: "Retrieval pipeline steps",
    query: "User query",
    vector: "Vector search · top {{n}}",
    fts: "Full-text search · top {{n}}",
    fusion: "RRF fusion",
    rerank: "Cross-encoder rerank",
    topN: "Top {{n}} → LLM (SSE)",
    primary: "Primary",
    fallback: "Fallback",
    primaryBody: "First reranker in the rerank routing chain. Every retrieval passes through it unless it fails over.",
    fallbackBody: "Used automatically when the primary is unhealthy, drained, or its p95 exceeds the threshold below.",
    noFallback: "No fallback reranker in the chain",
    noReranker: "No reranker configured",
    maxBatch: "Max batch (pairs)",
    failover: "Fail over when primary p95 >",
    ms: "{{value}} ms",
    settingsTitle: "Candidate & top-N settings",
    candidates: "Candidates per retriever",
    candidatesHint: "Vector and full-text search each return this many before fusion.",
    topNLabel: "Top-N after rerank",
    topNHint: "Chunks passed into the prompt.",
    minRelevance: "Minimum relevance score",
    minRelevanceHint: "Below this, the agent says it doesn't know and logs a knowledge gap.",
    save: "Save",
    saved: "Retrieval settings saved · applies to new requests",
  },
  guardrails: {
    injectionTitle: "Prompt-injection classifier",
    injectionBody: "Scans user messages and retrieved web-crawl chunks for instructions like \"ignore previous instructions\".",
    injectionToggle: "Prompt-injection classifier enabled",
    threshold: "Block threshold",
    action: "Action",
    actions: { block: "Block & reply with safe message", flag: "Flag only (log to traces)" },
    piiTitle: "PII detector",
    piiBody: "Redacts personal data in stored transcripts and traces (Data Privacy Act 2012).",
    piiToggle: "PII detector enabled",
    entitiesLabel: "PII entities to redact",
    entities: {
      ph_mobile: "PH mobile (09xx)",
      email: "Email",
      tin: "TIN",
      sss_gsis: "SSS / GSIS",
      philhealth: "PhilHealth",
      lrn: "LRN",
      home_address: "Home address",
      card_number: "Card numbers",
    },
    mask: "Also mask before sending to LLM",
    moderationTitle: "Moderation",
    moderationBody: "Classifies inputs and outputs per category. Lower thresholds are stricter.",
    moderationToggle: "Moderation enabled",
    categories: { self_harm: "Self-harm", sexual_minors: "Sexual / minors", harassment: "Harassment", violence: "Violence" },
    hotline: "Self-harm hits show PH hotlines (NCMH 1553) and alert the tenant's handoff team.",
    saveTitle: "Guardrail settings",
    saveHint: "Thresholds apply to new requests across all tenants.",
    save: "Save all",
    saved: "Guardrail thresholds saved · logged as guardrails.updated",
  },
  cache: {
    title: "Semantic cache",
    hint: "Keyed per tenant + agent + audience · answers reused only when sources are unchanged",
    toggle: "Semantic cache enabled",
    similarity: "Similarity threshold",
    similarityHint: "Cosine similarity of the query embedding. Lower = more hits, more risk of wrong reuse.",
    ttl: "TTL",
    ttlHint: "Entries are also purged when any cited source is re-ingested.",
    ttls: { "6": "6 hours", "24": "24 hours", "168": "7 days" },
    skip: "Skip cache for personalised answers (signed-in users, tool calls)",
    save: "Save",
    saved: "Cache settings saved",
    purge: "Purge cache…",
    statsTitle: "Cache stats",
    hitRateLabel: "Cache hit rate (30 days)",
    hitRate: "Hit rate (30d)",
    hits: "Hits",
    entries: "Entries",
    saved30: "Saved (30d)",
    topTitle: "Top cached questions",
    topItem: "{{question}} · {{tenant}}",
    topEmpty: "No cached questions yet.",
    purgeTitle: "Purge semantic cache?",
    purgeBody: "Cached answers are deleted. Expect higher latency and LLM spend while the cache warms up.",
    scope: "Scope",
    scopeAll: "All tenants ({{entries}} entries)",
    scopeTenant: "{{name}} only",
    purgeLabel: "Type PURGE to confirm",
    purgeSubmit: "Purge cache",
    purged_one: "Cache purged · {{count}} entry removed · logged as cache.purged",
    purged_other: "Cache purged · {{count}} entries removed · logged as cache.purged",
  },
} as const;
